<template>
    <loader :loader="isLoading" v-if="isLoading" />
    <div class="animated fadeIn" v-else>
        <b-card>
            <b-container fluid>
                <b-row no-gutters>
                    <b-col>
                        <c-target-header
                            :target="target"
                            :current-user="currentUser"
                            :current-event-permissions="permissionsGoal"
                            :isAllowedToEdit="targetCanBeEdited"
                            @view-evaluation="openCollapse"
                            @updated="getTargetFromAPI()"
                            :indicatorType="'target'"
                        />
                    </b-col>
                </b-row>
            </b-container>
        </b-card>
        <div :key="targetEvaluation.type" v-for="targetEvaluation in allTargetEvaluations">
            <div v-if="targetEvaluation.show">
                <b-card>
                    <b-container fluid>
                        <b-row no-gutters>
                            <b-col>
                                <c-target-evaluation-content
                                    :target="target"
                                    :isAdmin="currentUserIsAdmin"
                                    :current-event-permissions="permissionsGoal"
                                    :current-user-id="currentUser.id"
                                    :evaluations="targetEvaluation.evaluations"
                                    :evaluations-type="targetEvaluation.type"
                                    @updated="getTargetFromAPI()"
                                />
                            </b-col>
                        </b-row>
                    </b-container>
                </b-card>
            </div>
        </div>
        <b-card>
            <b-container fluid>
                <b-row>
                    <b-col md="4" xl="3">
                        <c-status :target="target" :isAllowedToEdit="canEdit" @updated="getTargetFromAPI()" />
                        <c-informations class="mb-3" :data="informations" />
                        <c-ranges :ranges="target.ranges" />
                        <c-tag class="mb-3" :data="tags" :target="target" :isAllowedToEdit="targetCanBeEdited" />
                    </b-col>
                    <b-col md="8" xl="9">
                        <b-button variant="primary" size="sm" class="pull-right" @click="openCommentAside(target)">{{
                            $t('targets.newComment')
                        }}</b-button>
                        <b-tabs>
                            <b-tab :title="$t('targets.details')">
                                <c-details
                                    :isAllowedToEdit="targetCanBeEdited"
                                    @update-parent="updateParent"
                                    :indicator="target"
                                />
                            </b-tab>
                            <b-tab :title="$t('targets.description')">
                                <template v-if="target.description">
                                    <p v-html="target.description"></p>
                                </template>
                                <b-alert show variant="info" v-else>
                                    {{ $t('targets.informations.noDescriptionToDisplay') }}
                                </b-alert>
                            </b-tab>
                            <b-tab @click="updateUnreadCommentsStatus" lazy>
                                <template slot="title">
                                    <span class="mr-1">
                                        {{ $t('targets.feed') }}
                                    </span>
                                    <b-badge v-if="unreadCommentsCount" variant="primary" class="align-text-bottom">
                                        {{ unreadCommentsCount }}
                                    </b-badge>
                                </template>
                                <c-feed
                                    @openModalCheckInEdit="openModalCheckInEdit"
                                    :targetId="target.id"
                                    :isAllowedToEdit="targetCanBeEdited"
                                />
                            </b-tab>

                            <b-tab :title="$t('targets.settings')" v-if="targetCanBeEdited">
                                <c-settings
                                    :target="target"
                                    :departments="target.departments"
                                    @update-parent="updateParent"
                                    :current-event-permissions="currentUser.permissions.goals"
                                />
                            </b-tab>
                            <b-tab :title="$t('targets.files')">
                                <c-files />
                            </b-tab>
                        </b-tabs>
                    </b-col>
                </b-row>
            </b-container>
        </b-card>
        <router-view :data="getModalData" @update-parent="updateParent"></router-view>
    </div>
</template>

<script>
import api from '@/services/api'
import Aside from '@/shared/aside'
import { getParam as getUrlParam } from '@/lib/url'
import { userPermissionsMixin } from '@/mixins'
import { getUserPermissions } from '@/services/user-permissions'
import Loader from '@/views/components/loader'
import i18n from '@/i18n'
import CTargetHeader from './components/target/target-header'
import CStatus from './components/target/side-menu/status'
import CRanges from '@/views/target/components/view/side-menu/ranges'
import CInformations from '@/views/target/components/view/side-menu/informations'
import CDetails from '@/views/target/components/view/tab-content/details'
import CFeed from './components/target/tab-content/feed'
import CSettings from './components/target/tab-content/settings'
import CFiles from './components/target/tab-content/files'
import CTag from './components/target/side-menu/tag'
import TargetCommentAside from '@/views/comment'
import CTargetEvaluationContent from './components/target/target-evaluation-content'

export default {
    name: 'Target',
    mixins: [userPermissionsMixin],
    components: {
        Loader,
        CTargetHeader,
        CStatus,
        CRanges,
        CInformations,
        CTag,
        CDetails,
        CFeed,
        CSettings,
        CFiles,
        CTargetEvaluationContent,
    },
    data() {
        return {
            id: getUrlParam('targetId', this),
            isLoading: true,
            informations: {},
            target: {},
            tags: [],
            unreadCommentsCount: null,
            unreadCommentsIds: null,
            allTargetEvaluations: [],
        }
    },
    mounted() {
        this.getTargetFromAPI()
    },
    methods: {
        getTargetFromAPI() {
            this.isLoading = true
            api.get(`/goals/${this.id}/edit`)
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp

                    const {
                        achieved,
                        allCommitteeEvaluations,
                        allManagerEvaluations,
                        calibrated_weight,
                        colors,
                        committeeEvaluation,
                        committees_users,
                        departments,
                        departments_focal_point_relations,
                        description,
                        formula,
                        formula_html,
                        has_check_in_available,
                        has_trigger_goal,
                        id,
                        initial_value,
                        is_calculated,
                        indicatorUsersIds,
                        kpi_checkins,
                        lastCheckInValue,
                        level,
                        managerEvaluation,
                        multiplier_achieved,
                        occupation_groups,
                        occupation_groups_focal_point_relations,
                        only_focal_point,
                        owner,
                        ownerEvaluation,
                        period,
                        polarity,
                        progress,
                        range,
                        ranges,
                        section,
                        status,
                        tags,
                        target,
                        target_library,
                        title,
                        type,
                        unit_of_measurement,
                        useProgressOnAutomaticCheckin,
                        user_relations,
                        user_focal_point_relations,
                        visualization_type,
                        weight,
                        year_to_date,
                        permissions_stage,
                        focalPointsPermission,
                    } = data

                    this.allTargetEvaluations = [
                        {
                            show: false,
                            type: 'selfEvaluate',
                            evaluations: [ownerEvaluation],
                        },
                        {
                            show: false,
                            type: 'managerEvaluate',
                            evaluations: allManagerEvaluations,
                        },
                        {
                            show: false,
                            type: 'committeeEvaluation',
                            evaluations: allCommitteeEvaluations,
                        },
                    ]

                    this.informations = {
                        id: owner.id,
                        owner: owner.name,
                        period: period.name,
                        managerId: owner.manager_id,
                        level: this.getLevel(level, section),
                        yearToDate: i18n.t(`yearToDate.${year_to_date.title}`),
                        hasTriggerGoal: this.getHasTriggerGoal(has_trigger_goal),
                        UnitOfMeasurement: unit_of_measurement.title,
                    }

                    this.target = {
                        achieved,
                        allCommitteeEvaluations,
                        allManagerEvaluations,
                        calibratedWeight: calibrated_weight,
                        colors,
                        committeeEvaluation,
                        committeesUsers: committees_users,
                        departments,
                        departmentsFocalPointRelations: departments_focal_point_relations,
                        description,
                        formula,
                        formulaHtml: formula_html,
                        hasCheckInAvailable: has_check_in_available,
                        hasTriggerGoal: has_trigger_goal,
                        id,
                        indicatorUsersIds,
                        initialValue: initial_value,
                        isCalculated: is_calculated,
                        kpiCheckins: kpi_checkins,
                        lastCheckInValue,
                        level,
                        managerEvaluation,
                        multiplierAchieved: multiplier_achieved,
                        occupationGroups: occupation_groups,
                        onlyFocalPoint: only_focal_point,
                        occupationGroupsFocalPointRelations: occupation_groups_focal_point_relations,
                        owner,
                        ownerEvaluation,
                        polarity,
                        progress,
                        period,
                        range,
                        ranges,
                        section,
                        status,
                        target,
                        targetLibrary: target_library,
                        title,
                        type,
                        useProgressOnAutomaticCheckin,
                        userRelations: user_relations,
                        userFocalPointRelations: user_focal_point_relations,
                        visualizationType: visualization_type,
                        weight,
                        yearToDate: year_to_date,
                        permissionsStage: permissions_stage,
                        focalPointsPermission: focalPointsPermission,
                    }
                    this.tags = tags
                    this.getUnreadComments()
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        getUnreadComments() {
            if (this.currentUser.id == this.target.owner.id) {
                api.get(
                    `notifications?filter=id&search=log_event.artifact_id:${this.id};log_event.artifact_type:goal;status:received,sent&searchFields=status:in;and=yes`
                ).then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    this.unreadCommentsCount = data.length ? data.length : null
                    this.unreadCommentsIds = data.map((unreadComment) => unreadComment.id)
                })
            }
        },
        updateUnreadCommentsStatus() {
            if (this.unreadCommentsCount) {
                api.put(`notifications`, {
                    ids: this.unreadCommentsIds,
                    status: 'viewed',
                }).then(() => {
                    this.unreadCommentsCount = null
                })
            }
        },
        getLevel(level, section) {
            return level == 'personal' ? i18n.t('groupLevel.personal') : section
        },
        getHasTriggerGoal(hasTriggerGoal) {
            return hasTriggerGoal ? i18n.t('targets.informations.yes') : i18n.t('targets.informations.no')
        },
        updateParent(action, data) {
            switch (action) {
                case 'updateTags':
                    this.tags = data ? data : []
                    break
                case 'updateCheckin':
                    this.getTargetFromAPI()
                    break
                case 'updateOnlyFocalPoint':
                    this.target.onlyFocalPoint = data.only_focal_point
                    this.target.userFocalPointRelations = data.user_focal_point_relations
                    this.target.departmentsFocalPointRelations = data.departments_focal_point_relations
                    this.target.occupationGroupsFocalPointRelations = data.occupation_groups_focal_point_relations
                    break
                case 'updateHasTriggerGoal':
                    this.target.hasTriggerGoal = data
                    break
                case 'updateSuggest':
                    this.target.departments = data.departments
                    this.target.occupationGroups = data.occupation_groups
                    this.target.userRelations = data.user_relations
                    this.target.targetLibrary = data.target_library
                    break
                case 'updateCalculation':
                    this.target.isCalculated = data.is_calculated
                    this.target.useProgressOnAutomaticCheckin = data.useProgressOnAutomaticCheckin
                    this.target.formula = data.formula
                    this.target.formulaHtml = data.formula_html
                    this.target.kpiCheckins = data.kpi_checkins
                    this.target.lastCheckInValue = data.lastCheckInValue
                    this.target.progress = data.progress
                    this.target.range = data.range
                    this.target.achieved = data.achieved
                    break
                default:
                    break
            }
        },
        openCommentAside() {
            Aside.open(TargetCommentAside, { target: this.target }, () => {})
        },
        openModalCheckInEdit(data) {
            const { targetId, checkinId } = data
            this.$router.push({
                path: `/v2/target/targets/view/${targetId}/checkin/${checkinId}`,
            })
        },
        openCollapse(type) {
            this.allTargetEvaluations.forEach((evaluation) => {
                if (type == evaluation.type) {
                    evaluation.show = !evaluation.show
                }
            })
        },
        didCurrentUserContractThisIndicator() {
            return this.target.indicatorUsersIds.includes(this.currentUser.id)
        },
        isTargetUnavailableToEdit() {
            return this.targetIsCompleted() || this.isPeriodFinished()
        },
        userHasRolePermission() {
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_target_master']
            const rolesPermission = this.userHasPermission(this.currentUser, availableRoles, [])
            return rolesPermission
        },
        targetIsCompleted() {
            return this.target.status == 'completed'
        },
        isPeriodFinished() {
            return this.target.period.status == 'finished'
        },
    },
    computed: {
        canEdit() {
            return this.userHasRolePermission()
                ? true
                : this.isCurrentUserFocalPointOfTarget
                ? false
                : this.permissionsGoal[this.target.level].edit
        },
        targetCanBeEdited() {
            if (this.userHasRolePermission()) {
                return true
            }
            return this.canEdit && !this.isTargetUnavailableToEdit()
        },
        isCurrentUserFocalPointOfTarget() {
            return this.target.userFocalPointRelations.some((user) => user.id == this.currentUser.id)
        },
        getModalData() {
            return {
                tags: this.tags,
                departments: this.target.departments,
                departmentsFocalPointRelations: this.target.departmentsFocalPointRelations,
                occupationGroups: this.target.occupationGroups,
                occupationGroupsFocalPointRelations: this.target.occupationGroupsFocalPointRelations,
                userRelations: this.target.userRelations,
                userFocalPointRelations: this.target.userFocalPointRelations,
                targetLevelPermissions: this.currentUser.permissions.goals[this.target.level],
                isCalculated: this.target.isCalculated,
                useProgressOnAutomaticCheckin: this.target.useProgressOnAutomaticCheckin,
                formula: this.target.formula,
                formulaHtml: this.target.formulaHtml,
                module: 'goal',
                indicatorId: this.target.id,
            }
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        currentUserIsAdmin() {
            return this.currentUser.role.name == 'administrator'
        },
        permissionsGoal() {
            if (this.userHasRolePermission()) {
                return getUserPermissions().current_event_permissions.goals
            }
            return this.currentUser.permissions.goals
        },
    },
}
</script>

<style>
button:disabled {
    cursor: not-allowed !important;
    pointer-events: all !important;
}
</style>
